import React, { useEffect } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import ReactGA from "react-ga4";

import "./App.scss";

import Home from "./components/Home/home";
import LandingBackground from "./components/LandingBackground/landingBackground";
import { MobilePersonalSettingsMenu } from "./components/Sidebar/MobileMenu/mobileMenu";

import LandingPage from "./pages/Account/LandingPage/landingPage";
import LandingPageForLinks from "./pages/Account/LandingPageForLinks/landingPage";
import CampaignNotFound from "./pages/Campaigns/Campaign/notFound";
import NotFound from "./components/NotFound/notFound";

import ChooseRecivingMethod from "./pages/Login/chooseRecivingMethod";
import LoginNotification from "./pages/Login/loginNotification";

import {
  ProtectedRoutes,
  LoginChecker,
  CommunityInvitation,
  ApproveCommunity,
  RedirectActiveCampaign,
  ConfirmInvitation,
} from "./helpers/AppHelpers/routeHelpers";

import {
  AccountComponents,
  AdminSettingsComponents,
  CampaignComponents,
  CommunityComponents,
  MemberComponents,
  OurBalanceComponents,
  SettingsComponents,
  TermsComponents,
  UnauthorizedComponents,
} from "./helpers/AppHelpers/components";

import MethodChosen from "./pages/PersonalSettings/PaymentPreferences/receivingMethodChosen";
import { uuidv4 } from "./helpers/functions";
import MyDonations from "./pages/PersonalSettings/MyDonations/myDonations";
// import NotificationsWrapper from "./pages/Notifications/notifications";
import CampaignsWrapper from "./pages/Campaigns/campaignsWrapperForAllMembers";
import Jobs from "./pages/Jobs";
import SuggestJob from "./pages/Jobs/suggestJob";
import ViewJob from "./pages/Jobs/viewJob";

ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);

function App() {
  useEffect(() => {
    const remember = JSON.parse(window.localStorage.getItem("remember"));
    const tab = window.sessionStorage.getItem("id");

    if (!tab && !remember) {
      window.sessionStorage.setItem("id", uuidv4());
    }
  }, []);

  return (
    <div className="App">
      <Router>
        <Switch>
          <Route exact path="/">
            <LoginChecker>
              <LandingPage />
            </LoginChecker>
          </Route>

          <Route exact path="/welcome">
            <LoginChecker>
              <LandingPageForLinks />
            </LoginChecker>
          </Route>

          <Route exact path="/login">
            <LoginChecker>
              <LandingBackground Component={AccountComponents.Login} />
            </LoginChecker>
          </Route>
          <Route exact path="/login/:token">
            <LoginChecker>
              <LandingBackground Component={AccountComponents.LinkInvitationLogin} />
            </LoginChecker>
          </Route>
          <Route path="/sign-up">
            <LoginChecker>
              <LandingBackground Component={AccountComponents.SignUp} />
            </LoginChecker>
          </Route>

          <Route exact path="/email-required">
            <LandingBackground Component={AccountComponents.EmailRequired} />
          </Route>

          <Route path="/create-account">
            <LoginChecker>
              <LandingBackground
                Component={AccountComponents.CreateAccount}
                color="blueBackground"
              />
            </LoginChecker>
          </Route>

          <Route path="/confirm-invitation">
            <ConfirmInvitation />
          </Route>

          <Route exact path="/change-email/verification">
            <LandingBackground Component={SettingsComponents.AccountInfo.VerificationEmail} />
          </Route>

          <Route path="/change-email">
            <LandingBackground Component={AccountComponents.Login} />
          </Route>

          <Route exact path="/reset-password">
            <LandingBackground Component={AccountComponents.ResetPassword} />
          </Route>
          <Route path="/change-password">
            <LandingBackground Component={AccountComponents.NewPassword} />
          </Route>

          <Route path="/profile/notification">
            <LandingBackground Component={LoginNotification} />
          </Route>

          <Route exact path="/privacy-and-policy">
            <TermsComponents.Privacy />
          </Route>
          <Route exact path="/terms-and-conditions">
            <TermsComponents.Terms />
          </Route>

          <Route path="/community-invitation">
            <CommunityInvitation />
          </Route>

          <Route exact path="/approved">
            <ApproveCommunity />
          </Route>

          <Route exact path="/active-campaign">
            <RedirectActiveCampaign />
          </Route>

          <Route exact path="/choose-receiving-method">
            <LandingBackground Component={ChooseRecivingMethod} color="blueBackground" />
          </Route>

          <Route exact path="/my-com/community/:communityId">
            <CampaignsWrapper />
          </Route>

          {/* UnauthorizedPages */}

          <Route exact path="/anonymous/campaigns/checkout">
            <LandingBackground Component={UnauthorizedComponents.Checkout} color="blueBackground" />
          </Route>
          <Route exact path="/anonymous/campaign-not-found">
            <LandingBackground Component={UnauthorizedComponents.NotFound} />
          </Route>
          <Route exact path="/anonymous/campaign-completed/:communityId/:campaignId/">
            <LandingBackground Component={CampaignNotFound} color="blueBackground" />
          </Route>
          <Route exact path="/anonymous/invite-friend">
            <LandingBackground Component={UnauthorizedComponents.NotFound} />
          </Route>
          {/* UnauthorizedPages ends */}

          {/* protected routes */}

          <Route exact path={"/my-donations"}>
            <ProtectedRoutes>
              <Home Component={MyDonations} />
            </ProtectedRoutes>
          </Route>

          {/* <Route exact path={"/notifications"}>
            <ProtectedRoutes>
              <Home Component={NotificationsWrapper} color="blueBackground" />
            </ProtectedRoutes>
          </Route> */}

          <Route exact path={"/jobs"}>
            <ProtectedRoutes>
              <Home Component={Jobs} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/jobs/suggest">
            <ProtectedRoutes>
              <Home Component={SuggestJob} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/jobs/view/:id">
            <ProtectedRoutes>
              <Home Component={ViewJob} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/method-already-chosen">
            <ProtectedRoutes>
              <LandingBackground Component={MethodChosen} color="blueBackground" />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/not-found">
            <ProtectedRoutes>
              <LandingBackground Component={NotFound} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/account-setup">
            <ProtectedRoutes>
              <LandingBackground
                Component={AccountComponents.AccountSetup}
                color="blueBackground"
              />
            </ProtectedRoutes>
          </Route>
          <Route path="/find-community">
            <ProtectedRoutes>
              <LandingBackground
                Component={AccountComponents.FindCommunity}
                color="blueBackground"
              />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/create-community">
            <ProtectedRoutes>
              <LandingBackground Component={AccountComponents.CreatCommunity} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/created/:communityId">
            <ProtectedRoutes>
              <LandingBackground Component={AccountComponents.Created} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/communities/all">
            <ProtectedRoutes>
              <Home Component={CommunityComponents.AllCommunities} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/communities/my">
            <ProtectedRoutes>
              <Home Component={CommunityComponents.MyCommunities} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/campaigns">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Campaigns} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/create-campaign">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Create} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/suggest-campaign">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Suggest} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/campaign/active/:communityId/:campaignId">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Active} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/campaign/active/:communityId/:campaignId/edit">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Edit} />
            </ProtectedRoutes>
          </Route>
          <Route exact path={"/campaigns/campaign/:communityId/:campaignId/invite-friend"}>
            <ProtectedRoutes>
              <Home Component={CampaignComponents.InviteFriend} color="blueBackground" />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/campaign/completed/:communityId/:campaignId">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Completed} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/campaign/repeat/:campaignId">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Repeat} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/:campaignId/donate/:communityId">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Amount} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/transfer/:campaignName/:campaignId">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Transfer} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/checkout">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.Checkout} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/thank-you">
            <ProtectedRoutes>
              <LandingBackground Component={CampaignComponents.ThankYou} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/campaigns/campaign-completed/:communityId/:campaignId">
            <ProtectedRoutes>
              <Home Component={CampaignNotFound} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/our-balance/donate">
            <ProtectedRoutes>
              <Home Component={OurBalanceComponents.Amount} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/our-balance/checkout">
            <ProtectedRoutes>
              <Home Component={OurBalanceComponents.Checkout} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/our-balance/thank-you">
            <ProtectedRoutes>
              <LandingBackground Component={OurBalanceComponents.ThankYou} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/communities/community/:communityId">
            <ProtectedRoutes>
              <Home Component={CommunityComponents.Community} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/communities/community/:communityId/edit">
            <ProtectedRoutes>
              <Home Component={CommunityComponents.Edit} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/communities/community/:communityId/add-members">
            <ProtectedRoutes>
              <Home Component={MemberComponents.AddMember} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/communities/community/:communityId/add-members/manual">
            <ProtectedRoutes>
              <Home Component={MemberComponents.AddMemberManual} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/communities/community/:communityId/member/:userId">
            <ProtectedRoutes>
              <Home Component={MemberComponents.MemberInfo} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/settings">
            <ProtectedRoutes>
              <Home Component={MobilePersonalSettingsMenu} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/admin/drafts/draft/:campaignId">
            <ProtectedRoutes>
              <Home Component={AdminSettingsComponents.DraftCampaign} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/admin/drafts/suggestion/:campaignId">
            <CampaignComponents.Draft />
          </Route>
          <Route exact path="/admin/drafts/suggestion/:communityId/:campaignId/add-member">
            <ProtectedRoutes>
              <Home Component={CampaignComponents.AddMember} />
            </ProtectedRoutes>
          </Route>

          <Route exact path="/login-security">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.AccountInfo.Security} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/personal-info">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.AccountInfo.Info} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/my-com">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.Communities.Find} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/new-community">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.Communities.New} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/new-community/created/:communityId">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.Communities.Created} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/payment-preferences">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.PaymentPreferences.Preferences} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/payment-method">
            <ProtectedRoutes>
              <LandingBackground
                Component={SettingsComponents.PaymentPreferences.RecievingMethod}
                color="blueBackground"
              />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/notifications">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.AccountInfo.Notifications} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/contact-us">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.ContactUs} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/tutorials">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.Tutorials} />
            </ProtectedRoutes>
          </Route>
          <Route exact path="/faq">
            <ProtectedRoutes>
              <Home Component={SettingsComponents.FAQ} />
            </ProtectedRoutes>
          </Route>

          <Route path="*">
            <Redirect to="/campaigns" />
          </Route>
        </Switch>
      </Router>
    </div>
  );
}

export default App;
